import type { StreamingFeatureFlags } from "./useStreamingFeatureFlags";
import type { StreamingType } from "@spwn/types";

/**
 * この端末で低遅延モードON時に使う再生方式。
 * "HLS" は低遅延手段なし (低遅延トグル非表示で、常に default の通常HLSを再生する)。
 */
export type PlayMode = "HLS" | "IVS" | "LLHLS";

/**
 * 配信データ・feature flag・端末能力から再生方式を決める。
 *
 * LL-HLS は、MMS でしか再生できない端末 (iPhone / iPad) では llhlsOnlyMmsDevices フラグ、
 * MSE 系 API を持つ端末では llhlsAllDevices フラグが有効なときに使う (2つのフラグは独立)。
 * LL-HLS が使えず IVS が再生可能なら IVS にフォールバックする。
 */
export const derivePlayMode = ({
  streamingType,
  hasLLHLSData,
  featureFlags,
  isAvailableOnlyMms,
  isAvailableMseOrMms,
  isIVSPlayable,
}: {
  streamingType: StreamingType;
  /** cookies の LL_HLS.url が非空か */
  hasLLHLSData: boolean;
  featureFlags: Pick<
    StreamingFeatureFlags,
    "isLLHLSOnlyMmsDevicesEnabled" | "isLLHLSAllDevicesEnabled"
  >;
  /** THEOplayer で MSE 再生に使えるのが ManagedMediaSource だけの端末 (iPhone / iPad) か */
  isAvailableOnlyMms: boolean;
  /** MediaSource または ManagedMediaSource が使えるか */
  isAvailableMseOrMms: boolean;
  /** IVS が再生可能か (IVS Player 対応 かつ Live かつ cookies に LL キーあり) */
  isIVSPlayable: boolean;
}): PlayMode => {
  if (streamingType !== "Live") {
    return "HLS";
  }
  const canPlayLLHLS =
    (isAvailableOnlyMms && featureFlags.isLLHLSOnlyMmsDevicesEnabled) ||
    (isAvailableMseOrMms && featureFlags.isLLHLSAllDevicesEnabled);
  if (hasLLHLSData && canPlayLLHLS) {
    return "LLHLS";
  }
  if (isIVSPlayable) {
    return "IVS";
  }
  return "HLS";
};

/**
 * 低遅延トグルの初期状態。低遅延が使えるならデフォルトONにする。
 * ただし MMS の挙動が不安定なため、LL-HLS を MMS で再生する端末 (iPhone / iPad) はデフォルトOFF。
 */
export const isLowLatencyDefaultEnabled = (
  mode: PlayMode,
  device: { isAvailableOnlyMms: boolean }
): boolean => {
  return mode !== "HLS" && !(mode === "LLHLS" && device.isAvailableOnlyMms);
};
